import { createConversationClient } from "@/api/agent-conversation";
import type { RunForestNode } from "@/api/config-migration";
import apiClient from "@/api/client";

/**
 * A conversa do agente com uma unidade da estrutura organizacional, o Nó 02 (spec
 * 2026-10-21, §9). Tipos, leitor do SSE e rotas são os do módulo genérico; o que é do
 * Nó 02 é o segmento `org-structure` da rota e o `context` da abertura (a unidade e os
 * ancestrais dela).
 *
 * Recusas da abertura, com o código no `detail`: 409 `conversation_already_open`,
 * `conversation_open_in_sscui`, `conversation_open_in_module_config`; 412
 * `cbc_connection_not_configured`, `cbc_connection_not_tested`; `parent_not_registered`
 * quando o pai ainda não está no CBC; 422 `invalid_context`, `context_not_supported`.
 */
export const orgStructureConversation = createConversationClient("org-structure");

/**
 * Como a abertura trata o roteiro gravado: `auto` (padrão) deixa o servidor decidir;
 * `script` é o sim do consultor ao roteiro pronto; `llm` é a recusa, com a conversa
 * com o agente.
 */
export type OrgScriptMode = "auto" | "script" | "llm";

/** A escolha do consultor diante da oferta (o `auto` não pergunta nada). */
export type OrgScriptChoice = Exclude<OrgScriptMode, "auto">;

/**
 * Resposta da rota `script-offer`: há roteiro determinístico gravado que serve à
 * unidade? `code`, `version` e `reason` vêm `null` quando não há.
 */
export interface OrgScriptOffer {
  available: boolean;
  code: string | null;
  version: number | null;
  reason: string | null;
}

/**
 * Pergunta ao servidor se há roteiro pronto para a unidade, antes de abrir a conversa.
 * O `context` é o mesmo da abertura (a unidade e os ancestrais dela).
 */
export async function fetchOrgScriptOffer(
  projectId: string,
  unit: RunForestNode,
  ancestors: RunForestNode[],
): Promise<OrgScriptOffer> {
  const response = await apiClient.post<OrgScriptOffer>(
    `/api/projects/${projectId}/config-migration/org-structure/script-offer`,
    { context: { unit, ancestors } },
  );
  return response.data;
}
